import { useState, useRef, useCallback, useEffect, type KeyboardEvent } from 'react';

interface InlineEditableTitleProps {
    className?: string;
    inputClassName?: string;
    maxLength?: number;
    onSave: (value: string) => void;
    placeholder?: string;
    value: string;
}

export function InlineEditableTitle({
    className = '',
    inputClassName = '',
    maxLength = 80,
    onSave,
    placeholder = 'Untitled chat',
    value
}: InlineEditableTitleProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);
    const hasSavedRef = useRef(false);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const save = useCallback(() => {
        if (hasSavedRef.current) return;
        hasSavedRef.current = true;

        const trimmed = editValue.trim();

        if (trimmed && trimmed !== value) {
            onSave(trimmed);
        }

        setIsEditing(false);
    }, [editValue, onSave, value]);

    const cancel = useCallback(() => {
        hasSavedRef.current = true;
        setEditValue(value);
        setIsEditing(false);
    }, [value]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                save();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
            }
        },
        [save, cancel]
    );

    const handleDoubleClick = useCallback(() => {
        hasSavedRef.current = false;
        setEditValue(value);
        setIsEditing(true);
    }, [value]);

    if (isEditing) {
        return (
            <input
                className={`bg-zinc-800 border border-zinc-600 rounded px-2 py-0.5 text-sm text-white outline-none focus:border-blue-500 ${inputClassName}`}
                maxLength={maxLength}
                ref={inputRef}
                type="text"
                value={editValue}
                onBlur={save}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
            />
        );
    }

    return (
        <span
            className={`truncate cursor-pointer hover:underline hover:decoration-zinc-500 ${className}`}
            title="Double-click to edit"
            onDoubleClick={handleDoubleClick}
        >
            {value || placeholder}
        </span>
    );
}
